import { FormikErrors } from 'formik';
import { outreach } from '@round/api';
import { FormValues } from './types';

/**
 * The form is flat and the payload is nested, so a field-keyed 400 has to be walked back onto a
 * form field. Every field the modal can hold is listed here, including those the target and filter
 * sections fill in, so no section has to carry error handling of its own.
 */
const API_PATH_TO_FIELD: Record<string, keyof FormValues> = {
    name: 'name',
    'targets.audios': 'audios',
    'targets.hashtags': 'hashtags',
    'outputs.email': 'outputEmail',
    'outputs.location': 'outputLocation',
    'outputs.rate': 'outputRate',
    'filters.locations': 'locations',
    'filters.location_match': 'locationMatch',
    'filters.min_followers': 'minFollowers',
    'filters.max_followers': 'maxFollowers',
    'filters.min_engagement_rate': 'minEngagementRate',
    'filters.min_avg_views': 'minAvgViews',
    deadline: 'deadline',
};

export type MappedApiErrors = {
    errors: FormikErrors<FormValues>;
    /**
     * Messages no control can show — `targets: [...]`, `client_id`, anything a later API version
     * adds. Dropping them would leave a rejected submit looking like nothing happened.
     */
    bannerMessage: string;
};

type ErrorLeaf = {
    path: string[];
    messages: string[];
};

export const mapApiErrorsToForm = (data: outreach.OutreachErrorResponseData): MappedApiErrors => {
    const errors: FormikErrors<FormValues> = {};
    const unmatched: string[] = [];

    getErrorLeaves(data).forEach(({ path, messages }) => {
        const field = getFieldForPath(path);
        if (!field) {
            unmatched.push(...messages);
            return;
        }

        // A list rejected item by item lands on the one control that holds the list, so the
        // messages accumulate rather than the last index winning.
        const existing = errors[field];
        errors[field] = joinMessages([typeof existing === 'string' ? existing : '', ...messages]);
    });

    return { errors, bannerMessage: joinMessages(unmatched) };
};

/** DRF messages are already sentences, so they only need a space between them. */
const joinMessages = (messages: string[]) => messages.filter(Boolean).join(' ');

/** Flattens the nested 400 into one entry per message list, keyed by the path that carried it. */
const getErrorLeaves = (detail: outreach.OutreachErrorDetail, path: string[] = []): ErrorLeaf[] => {
    if (Array.isArray(detail)) {
        return [{ path, messages: detail.map(String) }];
    }

    return Object.entries(detail).flatMap(([key, value]) => getErrorLeaves(value, [...path, key]));
};

/**
 * `targets.audios.0` belongs to the same control as `targets.audios`, so the longest known prefix
 * of the path wins and the index is discarded.
 */
const getFieldForPath = (path: string[]): keyof FormValues | undefined => {
    for (let length = path.length; length > 0; length--) {
        const field = API_PATH_TO_FIELD[path.slice(0, length).join('.')];
        if (field) {
            return field;
        }
    }

    return undefined;
};
